import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, LinearProgress, useMediaQuery, Button, IconButton, Avatar, Collapse } from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import RadarChart2 from '../../components/radarChart2'; // Asegúrate de que el componente del gráfico esté implementado.
import theme from '../../styles/StyleTheme'; // Importa tu archivo de estilos.
import { useNavigate, useParams } from 'react-router-dom';
import buildingsBackground from '../../assets/images/HIR-FONDO.png';
import { publicAxios } from '../../apiClients';
import BackButton from '../../components/BackButton';
import Loading from '../../components/Loading';
import { generateOrganizationRouteSegments } from '../../utils/slugify';
import HMLogo from '../../assets/banners/HM-blanco.png';

const DEFAULT_PROFILE_URL = 'https://i.pinimg.com/474x/15/0f/a8/150fa8800b0a0d5633abc1d1c4db3d87.jpg?nii=t';
const MarketOrganization = () => {
    const navigate = useNavigate();
    const { countrySlug, organizationSlug } = useParams();
    const [firm, setFirm] = useState(null);
    const [persons, setPersons] = useState([]);
    const [showPersons, setShowPersons] = useState(false);
    const personsRef = useRef(null);
    const [firmId, setFirmId] = useState(null);
    const [isResolvingSlug, setIsResolvingSlug] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Uso de useMediaQuery para detectar pantallas pequeñas
    const isMobile = useMediaQuery('(max-width:600px)'); // Ajusta el breakpoint según necesites

    useEffect(() => {
        window.scrollTo(0, 0);
        setError(null);
        setFirmId(null);
        setFirm(null);
        setPersons([]);
        setIsResolvingSlug(true);
        setIsLoading(true);

        if (!organizationSlug) {
            const storedOrganizationId = localStorage.getItem('organizationId');
            if (storedOrganizationId) {
                setFirmId(storedOrganizationId);
                setIsResolvingSlug(false);
                return;
            }
            setError('Organization identifier is missing.');
            setIsResolvingSlug(false);
            setIsLoading(false);
            return;
        }

        const resolveOrganizationId = async () => {
            try {
                const response = await publicAxios.get('/institution/market-monitor-health/table');
                let strictMatch = null;
                let looseMatch = null;

                response.data.forEach((item) => {
                    const segments = generateOrganizationRouteSegments({
                        institutionName: item.institutionName,
                        countryName: item.country?.name ?? item.Country?.name,
                        countryId: item.country?.id ?? item.Country?.id,
                        organizationId: item.id,
                    });

                    if (
                        countrySlug &&
                        !strictMatch &&
                        segments.organizationSlug === organizationSlug &&
                        segments.countrySlug === countrySlug
                    ) {
                        strictMatch = item;
                    }

                    if (!looseMatch && segments.organizationSlug === organizationSlug) {
                        looseMatch = item;
                    }
                });

                const matchedFirm = strictMatch ?? looseMatch;

                if (!matchedFirm) {
                    setError('Unable to find the requested organization.');
                    setIsLoading(false);
                    return;
                }

                setFirmId(matchedFirm.id);
                localStorage.setItem('organizationId', matchedFirm.id);
            } catch (fetchError) {
                console.error('Error resolving organization slug:', fetchError);
                setError('Unable to load the requested organization.');
                setIsLoading(false);
            } finally {
                setIsResolvingSlug(false);
            }
        };

        resolveOrganizationId();
    }, [organizationSlug, countrySlug]);

    useEffect(() => {
        if (!firmId) {
            setIsLoading(isResolvingSlug);
            return;
        }
        const fetchFirm = async () => {
            try {
                const response = await publicAxios.get(`/institution/market-monitor-health/${firmId}`);
                setFirm(response.data);
            localStorage.setItem('organizationId', firmId);
            } catch (fetchError) {
                console.error('Error fetching firm data:', fetchError);
                setError('Unable to load the requested organization.');
            }
        };
        const fetchPersons = async () => {
            try {
                const response = await publicAxios.get(`/institution/market-monitor-health/${firmId}/persons`);
                setPersons(response.data);
            } catch (fetchError) {
                console.error('Error fetching persons data:', fetchError);
            }
        };
        const fetchData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([fetchFirm(), fetchPersons()]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [firmId, isResolvingSlug]);

    const handleClickBalance = () => {
        navigate('/market-comparative-health');
    };

    useEffect(() => {
        if (showPersons && personsRef.current) {
            const timeout = setTimeout(() => {
                personsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [showPersons]);


    // Estados para controlar la expansión de cada box
    const [isExpanded, setIsExpanded] = useState({
        reputation: false,
        brandPower: false,
        trajectory: false,
        arena: false,
    });

    // Función para manejar el clic en "Ver más"
    const handleSeeMore = (key) => {
        setIsExpanded((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
        }));
    };

    // Textos para cada box
    const boxTexts = {
        reputation:
            "Institution's performance in major industry rankings, such as Neewsweek Ranking, among others.",
        brandPower: "The ability of institutions to inspire trust, stand out in the health industry, and build patient loyalty through its reputation, expertise, and values.",
        trajectory: 'Recognizes the time that key professionals have been operating in the field.',
        arena: 'Measures the market conditions in which the institution operates.',
    };

    // Datos para cada box
    const boxes = [
        { key: 'reputation', title: 'Reputation', value: firm?.reputation || 0, gridColumn: '3', gridRow: '1' },
        { key: 'brandPower', title: 'Visibility', value: firm?.brandPower || 0, gridColumn: '4', gridRow: '1' },
        { key: 'trajectory', title: 'Trajectory', value: firm?.trajectory || 0, gridColumn: '3', gridRow: '2' },
        { key: 'arena', title: 'Arena', value: firm?.arena || 0, gridColumn: '4', gridRow: '2' },
    ];

    const groupedPersons = persons.reduce((acc, person) => {
        if (!acc[person.area]) {
            acc[person.area] = [];
        }
        acc[person.area].push(person);
        return acc;
    }, {});

    if (isLoading) {
        return (
            <Loading
                background="#6ec1e4"
                barTrackColor="rgba(255, 255, 255, 0.45)"
                barColor="#3592D2"
                logo={HMLogo}
                logoAlt="Health Market Monitor"
            />
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ color: '#333333' }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    backgroundImage: `url(${buildingsBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    paddingX: isMobile ? '0%' : '5%',
                }}
            >
                <Grid container justifyContent="start" sx={{ mt: 1, ml: 0, mb: -3 }}>
                    <BackButton 
                    style={{
                        color: '#141a66',}}
                    
                    path={-1} />
                </Grid>
                <Box sx={{ p: 4 }}>
                    {/* Encabezado */}
                    <Box
                        sx={{
                            backgroundColor: '#6ec1e4',
                            borderRadius: 2,
                            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                            padding: 3,
                            marginBottom: 3,
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            {/* Columna 1: Nombre */}
                            <Grid item xs={12} sm={4}>
                                {firm && (
                                    <>
                                        <Typography variant="h5" sx={{ color: '#054169', fontWeight: 'bold' }}>
                                            {firm.institutionName}
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{ color: '#333333' }}>
                                            {firm?.Country?.name ?? firm?.country?.name}
                                        </Typography>
                                    </>
                                )}
                            </Grid>

                            {/* Columna 2: Otros datos */}
                            <Grid
                                item
                                xs={12}
                                sm={5}
                                sx={{ mt: { xs: 2, sm: 0 }, textAlign: { xs: 'left', sm: 'right' } }}
                            >
                                {/* #054169 */}
                                <Typography variant="body1" sx={{ color: '#333333' }}>
                                    Score:{' '}
                                    <span
                                        style={{
                                            color: '#054169',
                                            display: 'inline-block',
                                            minWidth: '50px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {firm?.totalScore ? firm.totalScore.toFixed(2) : 'N/A'}
                                    </span>
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#333333' }}>
                                    Local Position:{' '}
                                    <span
                                        style={{
                                            color: '#054169',
                                            display: 'inline-block',
                                            minWidth: '50px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {firm?.localPosition || 'N/A'}
                                    </span>
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#333333' }}>
                                    Global Position:{' '}
                                    <span
                                        style={{
                                            color: '#054169',
                                            display: 'inline-block',
                                            minWidth: '50px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {firm?.globalPosition || 'N/A'}
                                    </span>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3} sx={{ mt: { xs: 2, sm: 0 }, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#054169',
                                        '&:hover': { backgroundColor: "#3592D2"},
                                        paddingX: '5%',
                                        width: isMobile ? '100%' : '60%',
                                    }}
                                    onClick={() => navigate('/market-comparative-health')}
                                >
                                    Compare with another organization
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Grilla principal */}
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                            gridTemplateRows: 'auto auto',
                        }}
                    >
                        {/* Gráfico que ocupa dos columnas y dos filas */}
                        <Grid
                            item
                            sx={{
                                gridColumn: isMobile ? '1' : '1 / span 2',
                                gridRow: isMobile ? '1' : '1 / span 2',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    backgroundColor: '#6ec1e4', /*'#03263D'*/
                                    borderRadius: 2,
                                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                                    padding: isMobile ? '0px' : '20px',
                                    height: '100%',
                                    minHeight: '400px',
                                }}
                            >
                                <RadarChart2 firm={firm} /> {/* Componente del gráfico */}

                                
                            </Box>
                        </Grid>

                        {/* Boxes con botón "Ver más" */}
                        {boxes.map((box) => (
                            <Grid
                                key={box.key}
                                item
                                sx={{
                                    gridColumn: isMobile ? '1' : box.gridColumn,
                                    gridRow: isMobile ? 'auto' : box.gridRow,
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: '#6ec1e4',
                                        padding: 2,
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        minHeight: '190px',
                                        maxHeight: '190px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}
                                >
                                    {/* Contenido del box */}
                                    <Box sx={{ position: 'absolute', top: 10, left: 0, right: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography
                                                variant="h7"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: isExpanded[box.key] ? '0.9rem' : '1rem',
                                                    transition: 'font-size 0.3s ease',
                                                    color: '#054169',
                                                }}
                                            >
                                                {box.title}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                color: "#3592D2",
                                                marginY: 0.8,
                                                fontSize: isExpanded[box.key] ? '1.5rem' : '2rem',
                                                transition: 'font-size 0.3s ease',
                                            }}
                                        >
                                            {box.value || 'N/A'}
                                        </Typography>
                                        {!isExpanded[box.key] && (
                                            <Box sx={{ width: '70%', margin: '0 auto', marginTop: 1.5 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={box.value || 0} // Default to 0 if box.value is undefined
                                                    sx={{
                                                        height: 10,
                                                        borderRadius: 5,
                                                        backgroundColor: '#DBEAF6',
                                                        '& .MuiLinearProgress-bar': { backgroundColor: '#3592D2' },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Botón "Ver más" */}
                                    <Button
                                        variant="text"
                                        sx={{
                                            position: 'absolute',
                                            bottom: isExpanded[box.key] ? '80px' : '10px',
                                            left: 0,
                                            right: 0,
                                            margin: '0 auto',
                                            textTransform: 'none',
                                            color: '#333333',
                                            fontSize: '0.8rem',
                                        }}
                                        onClick={() => handleSeeMore(box.key)}
                                    >
                                        {isExpanded[box.key] ? 'See less' : 'See more'}
                                    </Button>

                                    {/* Texto adicional */}
                                    {isExpanded[box.key] && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                position: 'absolute',
                                                bottom: '10px',
                                                left: 10,
                                                right: 10,
                                                fontSize: '0.8rem',
                                                color: '#333333',
                                            }}
                                        >
                                            {boxTexts[box.key]}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {persons.length > 0 && (
                    <>
                        {/* boton see more + tabla */}
                        {/* <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#6ec1e4',
                                    color: 'black',
                                    '&:hover': { backgroundColor: "#3592D2" },
                                    textTransform: 'none',
                                    mb: 3,

                                }}
                                onClick={() => setShowPersons((prev) => !prev)}
                            >
                                {showPersons ? 'See Less' : 'See More'}
                            </Button>
                        </Box> */}
                        <Collapse in={showPersons} timeout="auto" unmountOnExit>
                            <Box
                                ref={personsRef}
                                sx={{
                                    mt: 3,
                                    mb: 4,
                                    px: 4,
                                    py: 2, // 👈 agrega espacio arriba y abajo
                                    backgroundColor: '#6ec1e4',
                                    borderRadius: 2,
                                    width: '100%',
                                    maxWidth: 600,
                                    margin: '0 auto',
                                }}
                            >
                                {Object.entries(groupedPersons).map(([area, people]) => (
                                    <Box key={area} sx={{ mb: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, color: '#333333', fontWeight: 'bold' }}>
                                            {area}
                                        </Typography>
                                        {people.map((person) => (
                                             <Box
                                                key={person.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    mb: 1,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        src={person.url_foto || DEFAULT_PROFILE_URL}
                                                        alt={`${person.nombre} ${person.apellido_paterno}`}
                                                        sx={{ width: 56, height: 56, mr: 2 }}
                                                    />
                                                    <Typography variant="body1" sx={{ color: '#333333' }}>
                                                        {person.nombre} {person.apellido_paterno} {person.apellido_materno}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ color: '#333333', fontWeight: 'bold' }}>
                                                    {person.calificacion}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </>
                )}

                {/* Contacto */}
                <Box
                    sx={{
                        backgroundColor: 'none',
                        paddingTop: 4,
                        paddingBottom: 13,
                        paddingX: 8,
                        borderRadius: 'none',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'center' : 'left' }}>
                        <Typography variant="body1" sx={{ color: '#333333', marginBottom: 1 }}>
                            Our AI model is fed with information coming from public sources or provided by the institutions (in this case, verified by our team). Are you a member in this place and want to update displayed information?
                        </Typography>
                    </Box>
                    <Box sx={{ ml: isMobile ? 0 : 4, mt: isMobile ? 2 : 0 }}>
                        <Button
                            component="a"
                            href="https://thelegalindustry.com/contact-us/"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            sx={{
                                backgroundColor: 'white',
                                color: '#333333',
                                borderRadius: 4,
                                width: '100%',
                                height: 30,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: "#3592D2" },
                            }}
                        >
                            Contact us
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default MarketOrganization;
